import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as projectApi from '../services/projectService';
import type { CreateProjectData, ProjectArchivedFilter } from '../services/projectService';
import { useOrgStore } from '../store/orgStore';

export const projectKeys = {
  all: (orgId?: string | null, archived: ProjectArchivedFilter = 'active') =>
    ['projects', orgId ?? 'none', archived] as const,
  detail: (id: string) => ['projects', 'detail', id] as const,
};

// CHG-132: archived 필터 옵션
export function useProjects(archived: ProjectArchivedFilter = 'active') {
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  return useQuery({
    queryKey: projectKeys.all(activeOrgId, archived),
    queryFn: () => projectApi.fetchProjects(archived),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => projectApi.fetchProject(id),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectData) => projectApi.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; data: Record<string, unknown> }) =>
      projectApi.updateProject(params.id, params.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(vars.id) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectApi.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// CHG-132: 프로젝트 보관 (3 모드)
export function useArchiveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      id: string;
      mode: projectApi.ArchiveMode;
      graduateSpaceName?: string;
    }) =>
      projectApi.archiveProject(params.id, {
        mode: params.mode,
        graduateSpaceName: params.graduateSpaceName,
      }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(vars.id) });
    },
  });
}

export function useAddMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; userId: string; projectRole: string }) =>
      projectApi.addProjectMember(params.projectId, params.userId, params.projectRole),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(vars.projectId) });
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; memberId: string }) =>
      projectApi.removeProjectMember(params.projectId, params.memberId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(vars.projectId) });
    },
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; memberId: string; projectRole: string }) =>
      projectApi.updateMemberRole(params.projectId, params.memberId, params.projectRole),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(vars.projectId) });
    },
  });
}
